/**
 * Subscription Product Selector Component
 * Handles subscription product selection and add to cart functionality
 */

class SubscriptionProductSelector {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.productData = this.getProductData();
    this.selectedOption = null;
    this.currentVariant = null;
    this.addToCartButton = element.querySelector('.subscription-add-to-cart');
    this.outOfStockButton = element.querySelector('.klaviyo-bis-trigger');
    this.variantChangeHandler = null;

    this.init();
  }

  init() {
    this.initializeCurrentVariant();
    this.bindEvents();
    this.setDefaultSelection();
    if (this.currentVariant) {
      this.handleVariantChange(this.currentVariant);
    }
    this.updateAddToCartButton();
    this.subscribeToVariantChanges();
  }

  getContextRoot() {
    return (
      this.element.closest('.quick-add-modal__content-info') ||
      this.element.closest('product-info') ||
      this.element.closest('section') ||
      document
    );
  }

  queryInContext(selector) {
    const root = this.getContextRoot();
    if (root && typeof root.querySelector === 'function') {
      const match = root.querySelector(selector);
      if (match) return match;
    }
    return document.querySelector(selector);
  }

  isLegacyQuickAddSectionMatch(eventSectionId) {
    if (!eventSectionId || !this.sectionId || !this.sectionId.startsWith('quickadd-')) {
      return false;
    }
    return this.sectionId.replace(/^quickadd-/, '') === eventSectionId;
  }

  getProductData() {
    const dataScript = this.queryInContext(`#subscription-product-data-${this.sectionId}`);
    if (!dataScript) {
      console.error('Subscription product data not found');
      return {};
    }

    try {
      return JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('Error parsing subscription product data:', error);
      return {};
    }
  }

  bindEvents() {
    // Handle radio button changes
    const radioButtons = this.element.querySelectorAll('input[type="radio"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', this.handleOptionChange.bind(this));
    });

    // Handle add to cart button click
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
    }
  }

  setDefaultSelection() {
    // Default to 1 month option if available, otherwise first available option
    const oneMonthRadio = this.element.querySelector('input[value="1month"]');
    if (oneMonthRadio && this.isOptionVisible(oneMonthRadio)) {
      oneMonthRadio.checked = true;
      this.selectedOption = this.getSelectedOptionData(oneMonthRadio);
    } else {
      const firstRadio = this.getFirstVisibleRadio();
      if (firstRadio) {
        firstRadio.checked = true;
        this.selectedOption = this.getSelectedOptionData(firstRadio);
      }
    }
  }

  handleOptionChange(event) {
    const radio = event.target;
    this.selectedOption = this.getSelectedOptionData(radio);
    this.updateAddToCartButton();

    // Dispatch custom event for other components to listen to
    this.element.dispatchEvent(new CustomEvent('subscription-option-changed', {
      detail: {
        selectedOption: this.selectedOption,
        sectionId: this.sectionId
      },
      bubbles: true
    }));
  }

  getSelectedOptionData(radio) {
    const optionType = radio.value;
    const sellingPlanId = radio.dataset.sellingPlanId;

    // Use the variant ID from the radio button's data attribute
    const parsedVariantId = parseInt(radio.dataset.variantId, 10);
    const variantId = Number.isNaN(parsedVariantId) ? null : parsedVariantId;

    let productData;
    switch (optionType) {
      case '1month':
        productData = this.productData.oneMonthProduct;
        break;
      case '3month':
        productData = this.productData.threeMonthProduct;
        break;
      case '6month':
        productData = this.productData.sixMonthProduct;
        break;
      case 'onetime':
      default:
        productData = this.productData.baseProduct;
        break;
    }

    return {
      type: optionType,
      variantId: variantId,
      sellingPlanId: sellingPlanId ? parseInt(sellingPlanId, 10) : null,
      productData: productData,
      isSubscription: optionType !== 'onetime'
    };
  }

  updateAddToCartButton() {
    if (!this.addToCartButton || !this.selectedOption) return;

    const selectedVariant = this.getSelectedVariant();
    const isAvailable = Boolean(selectedVariant?.available);

    this.addToCartButton.disabled = !isAvailable;

    if (isAvailable) {
      // Calculate total price for the selected option
      const totalPrice = this.calculateTotalPrice();
      if (totalPrice > 0) {
        this.addToCartButton.textContent = `ADD TO CART - $${(totalPrice / 100).toFixed(2)}`;
      } else {
        // Fallback if calculation fails
        this.addToCartButton.textContent = 'ADD TO CART';
        console.error('Failed to calculate price for option:', this.selectedOption);
      }
      this.addToCartButton.classList.remove('btn--disabled');
      if (this.outOfStockButton) {
        this.outOfStockButton.classList.add('hidden');
      }
    } else {
      this.addToCartButton.textContent = 'Sold Out';
      this.addToCartButton.classList.add('btn--disabled');
      if (this.outOfStockButton) {
        this.outOfStockButton.classList.remove('hidden');
      }
    }
  }

  async handleAddToCart() {
    if (!this.selectedOption) {
      return;
    }

    const selectedVariant = this.getSelectedVariant();
    const isAvailable = Boolean(selectedVariant?.available);

    if (!isAvailable) {
      return;
    }

    // Show loading state
    const originalText = this.addToCartButton.textContent;
    this.addToCartButton.textContent = 'Adding...';
    this.addToCartButton.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', this.selectedOption.variantId);
      formData.append('quantity', 1);

      if (this.selectedOption.sellingPlanId) {
        formData.append('selling_plan', this.selectedOption.sellingPlanId);
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Dispatch success event
      this.element.dispatchEvent(new CustomEvent('subscription-added-to-cart', {
        detail: {
          result: result,
          selectedOption: this.selectedOption,
          sectionId: this.sectionId
        },
        bubbles: true
      }));

      // Update cart if cart drawer/notification exists
      this.updateCart();

    } catch (error) {
      console.error('Error adding to cart:', error);

      // Dispatch error event
      this.element.dispatchEvent(new CustomEvent('subscription-add-to-cart-error', {
        detail: {
          error: error,
          selectedOption: this.selectedOption,
          sectionId: this.sectionId
        },
        bubbles: true
      }));

      // Show error state briefly
      this.addToCartButton.textContent = 'Error - Try Again';
      setTimeout(() => {
        const totalPrice = this.calculateTotalPrice();
        this.addToCartButton.textContent = `ADD TO CART - $${(totalPrice / 100).toFixed(2)}`;
      }, 2000);
    } finally {
      // Restore button state
      setTimeout(() => {
        this.addToCartButton.disabled = false;
        const totalPrice = this.calculateTotalPrice();
        this.addToCartButton.textContent = `ADD TO CART - $${(totalPrice / 100).toFixed(2)}`;
      }, 1000);
    }
  }

  async updateCart() {
    try {
      // Update cart count in header if it exists
      const cartCountElements = document.querySelectorAll('.cart-count-bubble');
      if (cartCountElements.length > 0) {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
          if (cart.item_count > 0) {
            element.classList.remove('hidden');
          }
        });
      }

      // Trigger cart drawer update if it exists
      if (window.cartDrawer) {
        window.cartDrawer.refresh();
      }

      // Trigger cart notification if it exists
      if (window.cartNotification) {
        window.cartNotification.open();
      }

    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  // Method to programmatically select an option
  selectOption(optionType) {
    const radio = this.element.querySelector(`input[value="${optionType}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change'));
    }
  }

  // Method to get current selection
  getSelectedOption() {
    return this.selectedOption;
  }

  // Method to refresh product data (useful for variant changes)
  refreshProductData() {
    this.productData = this.getProductData();
    this.updateAddToCartButton();
  }

  // Initialize current variant from the main product form
  initializeCurrentVariant() {
    const pickerVariant = this.getCurrentVariantFromPicker();
    if (pickerVariant) {
      this.currentVariant = this.findVariantById(pickerVariant.id);
      return;
    }

    const variantInput = this.queryInContext(
      `#product-form-${this.sectionId} input[name="id"], #product-form-${this.sectionId} select[name="id"]`
    );
    if (variantInput) {
      const variantId = parseInt(variantInput.value, 10);
      this.currentVariant = this.findVariantById(variantId);
    }
  }

  getCurrentVariantFromPicker() {
    const picker = this.queryInContext(
      `#variant-radios-${this.sectionId}, #variant-selects-${this.sectionId}, variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
    );
    if (!picker) {
      return null;
    }

    const jsonElement = picker.querySelector('[type="application/json"]');
    if (!jsonElement) {
      return null;
    }

    let variants = [];
    try {
      variants = JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.error('Error parsing picker variant data:', error);
      return null;
    }

    let selectedOptions = [];
    const pickerTag = picker.tagName.toLowerCase();

    if (pickerTag === 'variant-radios') {
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      selectedOptions = fieldsets.map((fieldset) => {
        const checkedRadio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((radio) => radio.checked);
        return checkedRadio ? checkedRadio.value : null;
      });
    } else {
      selectedOptions = Array.from(picker.querySelectorAll('select')).map((select) => select.value);
    }

    if (!selectedOptions.length || selectedOptions.some((value) => !value)) {
      return null;
    }

    return variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return variant.options.every((option, index) => option === selectedOptions[index]);
    }) || null;
  }

  // Find variant by ID in the base product variants
  findVariantById(variantId) {
    if (!this.productData.baseProduct?.variants) return null;
    return this.productData.baseProduct.variants.find(variant => variant.id === variantId) || null;
  }

  // Subscribe to custom variant change events
  subscribeToVariantChanges() {
    this.variantChangeHandler = (event) => {
      const eventSectionId = event.detail?.sectionId;
      const eventOriginalSectionId = event.detail?.originalSectionId;
      const isDirectMatch = eventSectionId === this.sectionId;
      const isLegacyQuickAddMatch = !eventOriginalSectionId && this.isLegacyQuickAddSectionMatch(eventSectionId);

      if (!isDirectMatch && !isLegacyQuickAddMatch) {
        return;
      }

      if (event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    };

    document.addEventListener('variant-changed', this.variantChangeHandler);
  }

  // Handle variant change event
  handleVariantChange(variant) {
    this.currentVariant = this.findVariantById(variant.id);
    this.updateVariantIdsOnRadioButtons(variant);
    this.refreshSelectedOption(); // Update selectedOption with new variant IDs
    this.updatePricesForVariant();
    this.updateAddToCartButton();
  }

  // Refresh the selected option data after variant IDs are updated
  refreshSelectedOption() {
    const checkedRadio = this.element.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      this.selectedOption = this.getSelectedOptionData(checkedRadio);
    }
  }

  // Update the data-variant-id attributes on radio buttons when variant changes
  updateVariantIdsOnRadioButtons(variant) {
    // Update one-time option to use base product variant
    const onetimeRadio = this.element.querySelector('input[value="onetime"]');
    if (onetimeRadio) {
      onetimeRadio.dataset.variantId = variant.id;
      onetimeRadio.dataset.sellingPlanId = '';
    }

    // For 1-month, 3-month and 6-month, find matching variants by title
    const baseVariant = this.findVariantById(variant.id);

    if (baseVariant) {
      // Update 1-month option
      const oneMonthRadio = this.element.querySelector('input[value="1month"]');
      if (oneMonthRadio && this.productData.oneMonthProduct) {
        const matchingOneMonth = this.productData.oneMonthProduct.variants.find(v => v.title === baseVariant.title);
        if (matchingOneMonth) {
          oneMonthRadio.dataset.variantId = matchingOneMonth.id;
          const allocation = matchingOneMonth.selling_plan_allocations?.[0];
          oneMonthRadio.dataset.sellingPlanId = allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
        } else {
          oneMonthRadio.dataset.variantId = '';
          oneMonthRadio.dataset.sellingPlanId = '';
        }
      }

      // Update 3-month option
      const threeMonthRadio = this.element.querySelector('input[value="3month"]');
      if (threeMonthRadio && this.productData.threeMonthProduct) {
        const matchingThreeMonth = this.productData.threeMonthProduct.variants.find(v => v.title === baseVariant.title);
        if (matchingThreeMonth) {
          threeMonthRadio.dataset.variantId = matchingThreeMonth.id;
          const allocation = matchingThreeMonth.selling_plan_allocations?.[0];
          threeMonthRadio.dataset.sellingPlanId = allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
        } else {
          threeMonthRadio.dataset.variantId = '';
          threeMonthRadio.dataset.sellingPlanId = '';
        }
      }

      // Update 6-month option
      const sixMonthRadio = this.element.querySelector('input[value="6month"]');
      if (sixMonthRadio && this.productData.sixMonthProduct) {
        const matchingSixMonth = this.productData.sixMonthProduct.variants.find(v => v.title === baseVariant.title);
        if (matchingSixMonth) {
          sixMonthRadio.dataset.variantId = matchingSixMonth.id;
          const allocation = matchingSixMonth.selling_plan_allocations?.[0];
          sixMonthRadio.dataset.sellingPlanId = allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
        } else {
          sixMonthRadio.dataset.variantId = '';
          sixMonthRadio.dataset.sellingPlanId = '';
        }
      }
    }
  }

  // Update all subscription option prices based on current variant
  updatePricesForVariant() {
    if (!this.currentVariant) {
      return;
    }

    // Update prices in the DOM for each subscription option

    // For each option type, find the corresponding variant from the correct product
    const baseVariantTitle = this.currentVariant.title;

    // Update 1-month option with its corresponding variant
    if (this.productData.oneMonthProduct) {
      const oneMonthVariant = this.productData.oneMonthProduct.variants.find(v => v.title === baseVariantTitle);
      if (oneMonthVariant) {
        this.updateOptionPrice('1month', oneMonthVariant);
      }
    }

    // Update 3-month option with its corresponding variant
    if (this.productData.threeMonthProduct) {
      const threeMonthVariant = this.productData.threeMonthProduct.variants.find(v => v.title === baseVariantTitle);
      if (threeMonthVariant) {
        this.updateOptionPrice('3month', threeMonthVariant);
      }
    }

    // Update 6-month option with its corresponding variant
    if (this.productData.sixMonthProduct) {
      const sixMonthVariant = this.productData.sixMonthProduct.variants.find(v => v.title === baseVariantTitle);
      if (sixMonthVariant) {
        this.updateOptionPrice('6month', sixMonthVariant);
      }
    }

    // Update onetime option with base product variant
    this.updateOptionPrice('onetime', this.currentVariant);

    this.syncSelectionAfterVisibilityUpdate();

    // Update the add to cart button price
    this.updateAddToCartButton();
  }

  // Update price display for a specific option
  updateOptionPrice(optionType, variant) {
    const optionElement = this.element.querySelector(`input[value="${optionType}"]`);
    if (!optionElement) {
      return;
    }

    const optionContainer = optionElement.closest('.subscription-option');
    if (!optionContainer) {
      return;
    }

    const totalPriceElement = optionContainer.querySelector('.subscription-option__total-price');
    const comparePriceElement = optionContainer.querySelector('.subscription-option__compare-price');
    const saveBadgeElement = optionContainer.querySelector('.subscription-option__save-badge');
    const onetimeLabelElement = optionContainer.querySelector('.subscription-option__frequency');

    if (!totalPriceElement) {
      return;
    }

    let allocationPrice = variant.price;
    let allocationCompareAtPrice = variant.compareAtPrice || variant.price;
    let discountPercentage = 0;
    let hasDiscount = false;

    // For subscription products, use the selling plan allocation from the variant
    if (optionType !== 'onetime') {
      const productData = this.getProductDataForOption(optionType);
      let allocation = null;

      // Use selling plan allocation from this specific variant
      if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
        allocation = variant.selling_plan_allocations[0];
        allocationPrice = allocation.price;
        allocationCompareAtPrice = allocation.compare_at_price;
      }

      hasDiscount = Number(allocationCompareAtPrice) > Number(allocationPrice);

      if (hasDiscount && productData?.priceAdjustments?.length > 0) {
        const adjustment = productData.priceAdjustments[0];
        if (adjustment.valueType === 'percentage' && adjustment.value > 0) {
          discountPercentage = adjustment.value;
        }
      }

      if (hasDiscount && discountPercentage === 0 && allocationCompareAtPrice > 0) {
        discountPercentage = Math.round(((allocationCompareAtPrice - allocationPrice) / allocationCompareAtPrice) * 100);
      }

      const shouldHide = !hasDiscount;
      optionContainer.classList.toggle('hidden', shouldHide);
      optionElement.disabled = shouldHide;
    } else {
      optionContainer.classList.remove('hidden');
      optionElement.disabled = false;
    }

    // Update DOM elements with selling plan allocation prices
    totalPriceElement.textContent = `$${(allocationPrice / 100).toFixed(2)}`;

    if (comparePriceElement) {
      if (hasDiscount) {
        comparePriceElement.textContent = `$${(allocationCompareAtPrice / 100).toFixed(2)}`;
        comparePriceElement.classList.remove('hidden');
      } else {
        comparePriceElement.classList.add('hidden');
      }
    }

    if (saveBadgeElement) {
      if (hasDiscount && discountPercentage > 0) {
        saveBadgeElement.textContent = `SAVE ${discountPercentage}%`;
        saveBadgeElement.classList.remove('hidden');
      } else {
        saveBadgeElement.classList.add('hidden');
      }
    }

    // Update one-time purchase label with new price
    if (optionType === 'onetime' && onetimeLabelElement) {
      onetimeLabelElement.textContent = `$${(allocationPrice / 100).toFixed(2)} + shipping`;
    }
  }

  // Get product data for a specific option type
  getProductDataForOption(optionType) {
    switch (optionType) {
      case '1month':
        return this.productData.oneMonthProduct;
      case '3month':
        return this.productData.threeMonthProduct;
      case '6month':
        return this.productData.sixMonthProduct;
      default:
        return this.productData.baseProduct;
    }
  }

  // Calculate total price for the selected option
  calculateTotalPrice() {
    if (!this.selectedOption) {
      return 0;
    }

    const productData = this.getProductDataForOption(this.selectedOption.type);

    // Find the variant for this selection
    if (productData?.variants) {
      const variant = productData.variants.find(v => v.id === this.selectedOption.variantId);

      if (variant) {
        // For subscription products, use the selling plan allocation price from variant
        if (this.selectedOption.type !== 'onetime' && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
          const allocationPrice = variant.selling_plan_allocations[0].price;
          return allocationPrice;
        }
        // For onetime purchases, use variant price
        return variant.price;
      }
    }

    return 0;
  }

  getSelectedVariant() {
    if (!this.selectedOption || !this.selectedOption.variantId) {
      return null;
    }

    const productData = this.getProductDataForOption(this.selectedOption.type);
    if (!productData?.variants) {
      return null;
    }

    return productData.variants.find(v => v.id === this.selectedOption.variantId) || null;
  }

  isOptionVisible(radio) {
    const optionContainer = radio?.closest('.subscription-option');
    return Boolean(optionContainer && !optionContainer.classList.contains('hidden'));
  }

  getFirstVisibleRadio() {
    const radios = Array.from(this.element.querySelectorAll('input[type="radio"]'));
    return radios.find((radio) => this.isOptionVisible(radio)) || null;
  }

  syncSelectionAfterVisibilityUpdate() {
    const checkedRadio = this.element.querySelector('input[type="radio"]:checked');
    if (checkedRadio && this.isOptionVisible(checkedRadio)) {
      this.selectedOption = this.getSelectedOptionData(checkedRadio);
      return;
    }

    const fallbackRadio = this.getFirstVisibleRadio();
    if (fallbackRadio) {
      fallbackRadio.checked = true;
      this.selectedOption = this.getSelectedOptionData(fallbackRadio);
    } else {
      this.selectedOption = null;
    }
  }

  // Cleanup method to unsubscribe from events
  destroy() {
    if (this.variantChangeHandler) {
      document.removeEventListener('variant-changed', this.variantChangeHandler);
      this.variantChangeHandler = null;
    }
  }
}

// Store instances for cleanup if needed
window.subscriptionProductSelectorInstances = [];

// Initialize all subscription selectors on the page
function initializeSubscriptionSelectors() {
  const selectors = document.querySelectorAll('.subscription-product-selector');
  selectors.forEach(element => {
    // Check if already initialized
    if (element.dataset.subscriptionSelectorInitialized) return;

    element.dataset.subscriptionSelectorInitialized = 'true';
    const instance = new SubscriptionProductSelector(element);
    window.subscriptionProductSelectorInstances.push(instance);
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSubscriptionSelectors);
} else {
  // DOM already loaded, initialize immediately
  initializeSubscriptionSelectors();
}

// Export for use in other scripts
window.SubscriptionProductSelector = SubscriptionProductSelector;
